import type { LabelPrintedPayload } from '@trackeat/shared';
import { stockEventMatchesBusiness } from '../stockEventBusinessScope.js';

export const LABEL_HISTORY_PAGE_SIZE = 50;
export interface LabelHistoryScope {
  tenantId: string | null;
  businessId: string | null;
  allowLegacyUnscoped: boolean;
}

/** Read model only. Deliberately lacks the complete payload required by print/void actions. */
export interface LabelHistoryHeader {
  id: string;
  business_id?: string | null;
  client_ts: string;
  rev?: number | null;
  payload: {
    business_id?: string | null;
    preset: string;
    lp_code?: string | null;
    produced_at?: string | null;
    expires_at?: string | null;
    data: Record<string, unknown>;
  };
}

export interface LabelHistoryEvent extends Omit<LabelHistoryHeader, 'payload'> {
  payload: LabelPrintedPayload & { business_id?: string | null };
}

export interface LabelHistorySnapshot {
  headers: LabelHistoryHeader[];
  voidedIds: string[];
}

interface HistoryDatabase {
  query(statement: string, values?: unknown[]): Promise<{ values?: Record<string, unknown>[] }>;
}

/** Only small search/identity fields cross the SQLite bridge, never ingredients or audit payloads. */
export async function readLabelHistory(db: HistoryDatabase, scope: LabelHistoryScope): Promise<LabelHistorySnapshot> {
  if (!scope.tenantId) return { headers: [], voidedIds: [] };
  const result = await db.query(`SELECT id, business_id, client_ts, rev, kind,
    json_object(
      'business_id', json_extract(payload, '$.business_id'),
      'preset', json_extract(payload, '$.preset'),
      'lp_code', json_extract(payload, '$.lp_code'),
      'produced_at', json_extract(payload, '$.produced_at'),
      'expires_at', json_extract(payload, '$.expires_at'),
      'data', json_object(
        'product', json_extract(payload, '$.data.product'),
        'production_id', json_extract(payload, '$.data.production_id'),
        'parent_production_id', json_extract(payload, '$.data.parent_production_id'),
        'material_lot_code', json_extract(payload, '$.data.material_lot_code'),
        'is_reprint', json_type(payload, '$.data.is_reprint') = 'true',
        'sample', json_type(payload, '$.data.sample') = 'true'
      )
    ) AS header_payload,
    json_extract(payload, '$.original_event_id') AS original_event_id
    FROM events_local
    WHERE (tenant_id = ? OR tenant_id IS NULL) AND kind IN ('label_printed', 'label_voided')
    ORDER BY rev DESC NULLS LAST, client_ts DESC, id ASC`, [scope.tenantId]);
  const headers: LabelHistoryHeader[] = [];
  const voidedIds = new Set<string>();
  for (const row of result.values ?? []) {
    const payload = JSON.parse(row.header_payload as string) as LabelHistoryHeader['payload'];
    const header = {
      id: row.id as string, business_id: row.business_id as string | null,
      client_ts: row.client_ts as string, rev: row.rev as number | null, payload,
    };
    if (!stockEventMatchesBusiness(header, scope.businessId, scope.allowLegacyUnscoped)) continue;
    if (row.kind === 'label_voided') {
      if (typeof row.original_event_id === 'string') voidedIds.add(row.original_event_id);
    } else {
      payload.data.is_reprint = payload.data.is_reprint === 1;
      payload.data.sample = payload.data.sample === 1;
      headers.push(header);
    }
  }
  return { headers, voidedIds: [...voidedIds] };
}

/** Complete immutable snapshots for visible rows or an explicit action, in requested ID order. */
export async function readLabelHistoryEvents(
  db: HistoryDatabase, scope: LabelHistoryScope, ids: readonly string[],
): Promise<LabelHistoryEvent[]> {
  if (!scope.tenantId || !ids.length) return [];
  const uniqueIds = [...new Set(ids)];
  const events = new Map<string, LabelHistoryEvent>();
  for (let offset = 0; offset < uniqueIds.length; offset += LABEL_HISTORY_PAGE_SIZE) {
    const batch = uniqueIds.slice(offset, offset + LABEL_HISTORY_PAGE_SIZE);
    const result = await db.query(`SELECT id, business_id, client_ts, rev, payload FROM events_local
      WHERE (tenant_id = ? OR tenant_id IS NULL) AND kind = 'label_printed'
      AND id IN (${batch.map(() => '?').join(',')})`, [scope.tenantId, ...batch]);
    for (const row of result.values ?? []) {
      const event = { ...row, payload: JSON.parse(row.payload as string) } as unknown as LabelHistoryEvent;
      if (stockEventMatchesBusiness(event, scope.businessId, scope.allowLegacyUnscoped)) events.set(event.id, event);
    }
  }
  return uniqueIds.flatMap(id => events.has(id) ? [events.get(id)!] : []);
}
